import React, { useEffect, useState } from 'react';
import { BookOpen, Download, FileText, Rocket, ChevronRight, ExternalLink, PlayCircle, Film } from 'lucide-react';
import AdminTrainingAnalytics from '../admin/AdminTrainingAnalytics';

/**
 * Help & Training panel — shows the eight ScensobConnect training manuals
 * (Worker / Employer / Agent / Admin × Quick-Start / Comprehensive) as cards
 * with inline PDF preview, direct download links, and short animated GIF
 * walkthroughs for each role.
 */
export default function HelpTraining({ role = 'worker' }) {
  const [manuals, setManuals] = useState([]);
  const [walkthroughs, setWalkthroughs] = useState([]);
  const [languages, setLanguages] = useState([{ code: 'en', name: 'English', coverage: 1.0 }]);
  const [loading, setLoading] = useState(true);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [videoTitle, setVideoTitle] = useState('');
  const [videoType, setVideoType] = useState('gif');
  const [error, setError] = useState(null);
  const [lang, setLang] = useState('en');
  const [downloadingBundle, setDownloadingBundle] = useState(false);

  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [manRes, wtRes, langRes] = await Promise.all([
          fetch(`${BACKEND}/api/training/manuals`, { credentials: 'include' }),
          fetch(`${BACKEND}/api/training/walkthroughs`, { credentials: 'include' }),
          fetch(`${BACKEND}/api/training/languages`, { credentials: 'include' }),
        ]);
        if (!manRes.ok) throw new Error(`HTTP ${manRes.status}`);
        const manData = await manRes.json();
        if (!cancelled) setManuals(manData?.manuals || []);
        if (wtRes.ok) {
          const wtData = await wtRes.json();
          if (!cancelled) setWalkthroughs((wtData?.walkthroughs || []).filter((w) => w.available));
        }
        if (langRes.ok) {
          const lData = await langRes.json();
          if (!cancelled && lData?.languages?.length) setLanguages(lData.languages);
        }
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Failed to load manuals');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [BACKEND]);

  const absUrl = (path) => `${BACKEND}${path}`;
  const withLang = (url) => (lang === 'en' ? url : `${url}${url.includes('?') ? '&' : '?'}lang=${lang}`);

  const grouped = manuals.reduce((acc, m) => {
    (acc[m.role] = acc[m.role] || []).push(m);
    return acc;
  }, {});

  const roleOrder = ['worker', 'employer', 'agent', 'admin'];
  const roleLabels = { worker: 'Workers', employer: 'Employers', agent: 'Agents', admin: 'Administrators' };
  const roleSubtitles = {
    worker: 'Find shifts, clock in, get paid',
    employer: 'Post shifts, manage rosters, run payroll',
    agent: 'Claim shifts, staff clients, earn commission',
    admin: 'Platform control tower · settlements, security, config',
  };
  const roleIcons = {
    worker: '👷',
    employer: '🏢',
    agent: '🤝',
    admin: '🛡️',
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-6" data-testid="help-training-panel">
      {/* Header */}
      <div className="mb-8">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-50 border border-cyan-100 text-cyan-700 text-xs font-semibold mb-3">
          <BookOpen className="w-3.5 h-3.5" />
          Help & Training
        </div>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Training Manuals</h1>
            <p className="text-slate-600 max-w-2xl">
              Every role gets a <b>Quick-Start Guide</b> to get productive fast, plus a
              <b> Comprehensive Manual</b> that documents every feature with real screenshots.
              Download the PDF for offline reading or preview it right here.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Language toggle — driven by /api/training/languages */}
            <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1" role="tablist">
              {languages.map((l) => (
                <button
                  key={l.code}
                  onClick={() => setLang(l.code)}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold transition ${
                    lang === l.code ? 'bg-slate-900 text-white' : 'text-slate-600 hover:text-slate-900'
                  }`}
                  data-testid={`lang-toggle-${l.code}`}
                  title={l.coverage < 1 ? `${Math.round(l.coverage * 100)}% translated` : undefined}
                >
                  {l.name}
                </button>
              ))}
            </div>
            {/* Role-only pack */}
            <a
              href={withLang(absUrl(`/api/training/bundle.zip?role=${role}`))}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-slate-200 hover:border-cyan-300 hover:bg-cyan-50 text-slate-700 text-xs font-semibold transition"
              data-testid="download-role-pack"
              title={`Only this role's 2 manuals + 2 walkthroughs`}
            >
              <Download className="w-4 h-4" />
              My Role Pack
            </a>
            {/* Full download all */}
            <a
              href={withLang(absUrl('/api/training/bundle.zip'))}
              onClick={() => {
                setDownloadingBundle(true);
                setTimeout(() => setDownloadingBundle(false), 4000);
              }}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-xs font-semibold shadow-sm transition"
              data-testid="download-training-bundle"
              title="Download all 8 manuals + walkthroughs as a ZIP"
            >
              <Download className="w-4 h-4" />
              {downloadingBundle ? 'Preparing…' : 'HR Handoff ZIP'}
            </a>
          </div>
        </div>
      </div>

      {loading && (
        <div className="text-slate-500 text-sm" data-testid="help-training-loading">
          Loading manuals…
        </div>
      )}

      {error && (
        <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="help-training-error">
          {error}
        </div>
      )}

      {!loading && !error && (
        <div className="space-y-8">
          {/* Admin-only analytics + refresh controls */}
          {role === 'admin' && <AdminTrainingAnalytics />}

          {/* Video walkthroughs for the current role — quick visual tour */}
          {walkthroughs.filter((w) => w.role === role).length > 0 && (
            <section
              className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-6 shadow-sm"
              data-testid={`walkthroughs-section-${role}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Film className="w-5 h-5 text-slate-700" />
                <h2 className="text-xl font-bold text-slate-900">Video walkthroughs</h2>
                <span className="text-[10px] font-bold uppercase tracking-wider bg-slate-900 text-white px-2 py-0.5 rounded-full">
                  New
                </span>
              </div>
              <p className="text-sm text-slate-600 mb-4">
                Watch each key flow in 6-10 seconds. No sound needed.
              </p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {walkthroughs.filter((w) => w.role === role).map((w) => (
                  <button
                    key={w.id}
                    onClick={() => {
                      // Prefer MP4 (H.264 + captions); fall back to GIF
                      if (w.mp4_url) {
                        setVideoUrl(absUrl(w.mp4_url));
                        setVideoType('mp4');
                      } else {
                        setVideoUrl(absUrl(w.gif_url || w.url));
                        setVideoType('gif');
                      }
                      setVideoTitle(w.title);
                    }}
                    className="group relative overflow-hidden rounded-xl border border-slate-200 bg-white hover:border-cyan-300 hover:shadow-md transition text-left"
                    data-testid={`walkthrough-btn-${w.id}`}
                  >
                    <div className="aspect-video bg-slate-900 relative overflow-hidden">
                      <img
                        src={absUrl(w.gif_url || w.url)}
                        alt={w.title}
                        className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/10 transition">
                        <PlayCircle className="w-12 h-12 text-white drop-shadow-lg" />
                      </div>
                      <span className="absolute bottom-2 right-2 text-[10px] font-bold px-1.5 py-0.5 bg-black/70 text-white rounded">
                        {w.duration}
                      </span>
                      {w.mp4_url && (
                        <span className="absolute top-2 left-2 text-[10px] font-bold px-1.5 py-0.5 bg-emerald-500 text-white rounded">
                          HD MP4
                        </span>
                      )}
                    </div>
                    <div className="p-3">
                      <p className="text-sm font-semibold text-slate-900 truncate">{w.title}</p>
                      <p className="text-[11px] text-slate-500 mt-0.5">Click to view with captions</p>
                    </div>
                  </button>
                ))}
              </div>
            </section>
          )}

          {roleOrder.filter((r) => grouped[r]?.length).map((r) => {
            const isCurrent = r === role;
            const cards = grouped[r] || [];
            return (
              <section
                key={r}
                className={`rounded-2xl border ${isCurrent ? 'border-cyan-300 bg-gradient-to-br from-cyan-50/60 to-white' : 'border-slate-200 bg-white'} p-6 shadow-sm`}
                data-testid={`help-training-section-${r}`}
              >
                <div className="flex items-start justify-between mb-4 gap-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-2xl">{roleIcons[r]}</span>
                      <h2 className="text-xl font-bold text-slate-900">For {roleLabels[r]}</h2>
                      {isCurrent && (
                        <span className="text-[10px] font-bold uppercase tracking-wider bg-cyan-500 text-white px-2 py-0.5 rounded-full">
                          Your role
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-600">{roleSubtitles[r]}</p>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  {cards.map((m) => {
                    const isQuick = m.depth === 'quickstart';
                    const url = withLang(absUrl(m.url));
                    return (
                      <div
                        key={`${m.role}-${m.depth}`}
                        className="rounded-xl border border-slate-200 bg-white p-5 hover:shadow-md hover:border-cyan-200 transition group"
                        data-testid={`manual-card-${m.role}-${m.depth}`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          {isQuick ? (
                            <Rocket className="w-5 h-5 text-orange-500" />
                          ) : (
                            <FileText className="w-5 h-5 text-indigo-600" />
                          )}
                          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                            isQuick
                              ? 'bg-orange-100 text-orange-700'
                              : 'bg-indigo-100 text-indigo-700'
                          }`}>
                            {isQuick ? 'Quick-Start' : 'Comprehensive'}
                          </span>
                        </div>
                        <h3 className="font-bold text-slate-900 mb-1">{m.title}</h3>
                        <p className="text-xs text-slate-500 mb-4">
                          {isQuick
                            ? `Get productive in under 10 minutes · ${m.pages}`
                            : `Every feature explained · ${m.pages}`}
                        </p>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setPreviewUrl(url)}
                            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-800 text-sm font-semibold transition"
                            data-testid={`manual-preview-${m.role}-${m.depth}`}
                          >
                            <BookOpen className="w-4 h-4" />
                            Preview
                          </button>
                          <a
                            href={url}
                            download={`scensobconnect-${m.role}-${m.depth}.pdf`}
                            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold transition"
                            data-testid={`manual-download-${m.role}-${m.depth}`}
                          >
                            <Download className="w-4 h-4" />
                            Download PDF
                          </a>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {/* Video walkthrough modal */}
      {videoUrl && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setVideoUrl(null)}
          data-testid="walkthrough-modal-overlay"
        >
          <div
            className="bg-slate-900 rounded-2xl shadow-2xl w-full max-w-4xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
              <div className="flex items-center gap-2 text-white text-sm font-semibold">
                <Film className="w-4 h-4" />
                {videoTitle}
              </div>
              <button
                onClick={() => setVideoUrl(null)}
                className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs font-semibold"
                data-testid="walkthrough-modal-close"
              >
                Close
              </button>
            </div>
            <div className="bg-black p-4 flex items-center justify-center">
              {videoType === 'mp4' ? (
                <video
                  src={videoUrl}
                  controls
                  autoPlay
                  loop
                  playsInline
                  className="max-w-full max-h-[70vh] rounded-lg"
                  data-testid="walkthrough-video"
                />
              ) : (
                <img src={videoUrl} alt={videoTitle} className="max-w-full max-h-[70vh] rounded-lg" />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Preview modal */}
      {previewUrl && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setPreviewUrl(null)}
          data-testid="manual-preview-overlay"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
              <div className="flex items-center gap-2 text-slate-700 text-sm font-semibold">
                <FileText className="w-4 h-4" />
                Training Manual Preview
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={previewUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-semibold"
                  data-testid="manual-preview-open-new-tab"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> Open in new tab
                </a>
                <button
                  onClick={() => setPreviewUrl(null)}
                  className="px-3 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold"
                  data-testid="manual-preview-close"
                >
                  Close
                </button>
              </div>
            </div>
            <iframe
              src={previewUrl}
              title="Training Manual"
              className="flex-1 w-full bg-slate-100"
              data-testid="manual-preview-frame"
            />
          </div>
        </div>
      )}

      <div className="mt-8 text-xs text-slate-400 flex items-center gap-1">
        <ChevronRight className="w-3 h-3" />
        Need something these manuals don&apos;t cover? Reach us at <a className="text-cyan-600 hover:underline" href="mailto:help@scensobconnect.com">help@scensobconnect.com</a>
      </div>
    </div>
  );
}
